import React, { useCallback, useEffect, useState } from 'react';
import { Instagram, Facebook, Globe2 } from 'lucide-react';
import api from '../../lib/api';
import { formatCurrency } from '../../mock';
import { PeriodBar, Ribbon, DataTable, PRESETS } from './shared';

export const sourceIcon = (src) => {
  const s = (src || '').toLowerCase();
  if (s.includes('instagram')) return Instagram;
  if (s.includes('facebook') || s.includes('meta')) return Facebook;
  return null;
};
const iconCls = (src) => {
  const s = (src || '').toLowerCase();
  if (s.includes('instagram')) return 'text-pink-400';
  if (s.includes('facebook') || s.includes('meta')) return 'text-sky-400';
  return 'text-muted-foreground';
};

const SocialCard = ({ label, row, Icon, cls }) => (
  <div className="ent-panel" data-testid={`plat-card-${label.toLowerCase()}`}>
    <div className="ent-panel-head"><Icon className={`w-3.5 h-3.5 ${cls}`} /><span className="ent-label">{label}</span></div>
    <div className="grid grid-cols-3 divide-x divide-border">
      <div className="px-3 py-3"><p className="ent-label">Leads</p><p className="ent-value num mt-1">{row.leads}</p></div>
      <div className="px-3 py-3"><p className="ent-label">Vendas</p><p className="ent-value num mt-1 t-ok">{row.sales}</p></div>
      <div className="px-3 py-3"><p className="ent-label">Conversão</p><p className="ent-value num mt-1">{row.conversion}%</p></div>
    </div>
    <div className="border-t border-border px-3 py-2 flex items-center justify-between">
      <span className="ent-label">Receita</span>
      <span className="num font-bold">{formatCurrency(row.revenue)}</span>
    </div>
  </div>
);

export const PlatformsReport = () => {
  const preset = PRESETS().find(([k]) => k === 'd30')[2];
  const [range, setRange] = useState(preset);
  const [d, setD] = useState(null);

  const load = useCallback(async (r = range) => {
    try {
      const p = new URLSearchParams({ date_from: r.from || '', date_to: r.to || '' });
      const { data } = await api.get(`/reports/platforms?${p.toString()}`);
      setD(data);
    } catch (e) { setD({ rows: [], social: {}, kpis: {} }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  useEffect(() => { load(preset); /* eslint-disable-next-line */ }, []);

  const k = d?.kpis || {};
  const s = d?.social || {};

  return (
    <div className="space-y-3" data-testid="platforms-report">
      <PeriodBar range={range} setRange={setRange} onApply={(r) => load(r || range)} testid="plat-period" />

      <Ribbon testid="plat-kpis" items={[
        ['Leads no período', k.leads ?? 0],
        ['Vendas no período', k.sales ?? 0],
        ['Leads Instagram + Facebook', k.social_leads ?? 0],
        ['Vendas Instagram + Facebook', k.social_sales ?? 0, 't-ok'],
        ['Receita das redes', formatCurrency(k.social_revenue || 0)],
        ['Receita total', formatCurrency(k.revenue || 0)],
      ]} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {s.Instagram && <SocialCard label="Instagram" row={s.Instagram} Icon={Instagram} cls="text-pink-400" />}
        {s.Facebook && <SocialCard label="Facebook" row={s.Facebook} Icon={Facebook} cls="text-sky-400" />}
        {s['Meta Ads'] && <SocialCard label="Meta Ads" row={s['Meta Ads']} Icon={Globe2} cls="text-primary" />}
      </div>

      <div className="ent-panel">
        <div className="ent-panel-head"><span className="ent-label">Todas as plataformas / fontes</span><span className="ent-pill">{d?.rows?.length || 0}</span></div>
        <DataTable testid="plat-table" rowKey="key" rows={d?.rows || []} empty="Sem leads ou vendas no período" cols={[
          { key: 'key', label: 'PLATAFORMA', render: (r) => {
            const Icon = sourceIcon(r.key);
            return (
              <span className="flex items-center gap-1.5 font-semibold">
                {Icon ? <Icon className={`w-3.5 h-3.5 ${iconCls(r.key)}`} /> : <Globe2 className="w-3.5 h-3.5 text-muted-foreground/50" />}
                {r.key}
              </span>
            );
          } },
          { key: 'leads', label: 'LEADS', align: 'right' },
          { key: 'contact_rate', label: 'CONTATO', align: 'right', render: (r) => `${r.contact_rate}%` },
          { key: 'appointments', label: 'APPOINTMENTS', align: 'right' },
          { key: 'sales', label: 'VENDAS', align: 'right', render: (r) => <span className="t-ok font-bold">{r.sales}</span> },
          { key: 'conversion', label: 'CONVERSÃO', align: 'right', render: (r) => <span className={r.conversion >= 10 ? 't-ok' : r.conversion > 0 ? '' : 't-label'}>{r.conversion}%</span> },
          { key: 'revenue', label: 'RECEITA', align: 'right', render: (r) => formatCurrency(r.revenue) },
          { key: 'avg_ticket', label: 'TICKET MÉDIO', align: 'right', render: (r) => formatCurrency(r.avg_ticket) },
          ...(d?.fin_view ? [{ key: 'gross', label: 'GROSS', align: 'right', render: (r) => <span className={r.gross > 0 ? 't-ok' : r.gross < 0 ? 't-bad' : 't-label'}>{formatCurrency(r.gross)}</span> }] : []),
        ]} />
      </div>
    </div>
  );
};

export default PlatformsReport;
